'use client';

import { FC, useEffect, useState, memo } from 'react';

import { Document } from '@contentful/rich-text-types';

import classNames from 'classnames';

import { Badge } from '@/components';

import useAuth from '@/context/AuthContext';

import { TypeInverseTextHeroFields } from '@/core/generated-types';

import { RichText } from '../RichText';
import { RichTextHeading } from '../RichTextHeading';
import { UniversalCta } from '../UniversalCta';

import styles from './InverseTextHero.module.scss';

const componentName = 'inverseTextHero';

const InverseTextHero: FC<TypeInverseTextHeroFields> = ({
  leadingText,
  mainText,
  mainRichText,
  cta,
  trailingText,
  backgroundColor,
  badge,
  descriptionTextLoggedOut,
  descriptionTextLoggedIn,
  isFullWidth,
}) => {
  const [hydrated, setHydrated] = useState(false);

  const { user } = useAuth();

  const useLoggedInText = !!user;

  const textToDisplay = useLoggedInText
    ? descriptionTextLoggedIn
    : descriptionTextLoggedOut;

  // This hydrated state is needed to avoid hydration errors due to SSR https://nextjs.org/docs/messages/react-hydration-error#solution-1-using-useeffect-to-run-on-the-client-only
  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <div className={`px-6 py-10 lg:py-12 bg-${backgroundColor?.fields?.color}`}>
      <div
        className={classNames('mx-auto text-center', {
          'max-w-[740px]': !isFullWidth,
        })}
      >
        {badge && (
          <div className="mb-3 lg:mb-4">
            <Badge {...badge.fields} />
          </div>
        )}

        {leadingText && (
          <div
            className="mb-2 text-lg font-bold md:text-2xl"
            data-testid={`${componentName}-leadingText`}
          >
            {leadingText}
          </div>
        )}

        {mainText && (
          <div
            className="text-h-x3 font-bold md:text-[42px] md:leading-[56px] [&_span]:font-bradford [&_span]:font-medium"
            data-testid={`${componentName}-mainText`}
            dangerouslySetInnerHTML={{ __html: mainText }}
          />
        )}

        {mainRichText && (
          <RichTextHeading
            classes={classNames({
              'text-h-x2 md:text-h-x1': isFullWidth,
            })}
            omitSizeString={isFullWidth}
            content={mainRichText as Document}
          />
        )}

        {cta && (
          <UniversalCta
            link={cta}
            testId={`${componentName}-cta`}
            className="mt-6 inline-flex"
          />
        )}

        {textToDisplay && hydrated && (
          <div className={`${styles.inverseTextHero} text-sm mt-4`}>
            <RichText content={textToDisplay as Document} />
          </div>
        )}

        {trailingText && hydrated && (
          <div className="mt-6 text-sm">
            <RichText content={trailingText as Document} />
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(InverseTextHero);
